// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LanguageSelector } from '@/i18n/LanguageSelector'
import { Branding } from '@qh/design-tokens/branding'

export function WelcomeScreen() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    if (searchParams.get('token') === 'expired') {
      navigate('/link-expired', { replace: true })
    }
  }, [searchParams, navigate])

  return (
    <div className="min-h-dvh flex flex-col bg-gradient-to-br from-[#012031] via-[#033C85] to-[#006EF9] relative overflow-hidden">
      {/* Animated background blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#02C7C3]/20 blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-15%] -left-32 w-[450px] h-[450px] rounded-full bg-[#006EF9]/25 blur-[100px] animate-pulse [animation-delay:1.2s]" />
        <div className="absolute top-[45%] left-[30%] w-[250px] h-[250px] rounded-full bg-white/5 blur-[80px] animate-pulse [animation-delay:2.4s]" />
      </div>

      {/* Top bar: QH mark + language picker */}
      <div className="relative z-10 flex items-center justify-between px-6 pt-12 sm:px-8">
        <div className="h-8 w-8 rounded-full bg-white/90 flex items-center justify-center shadow-[0_0_20px_rgba(255,255,255,0.3)]">
          <span className="text-[#006EF9] text-[10px] font-black leading-none tracking-tight">QH</span>
        </div>
        <LanguageSelector variant="ghost" />
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col flex-1 items-center justify-center px-6 pb-16 sm:px-8">
        <div className="w-full max-w-xs flex flex-col items-center text-center">

          {/* QH hero circle with glow */}
          <div className="relative mb-3">
            <div className="absolute inset-0 rounded-full bg-[#02C7C3]/40 blur-2xl scale-[2] animate-pulse" />
            <div className="relative h-[88px] w-[88px] rounded-full bg-gradient-to-br from-[#006EF9] to-[#02C7C3] flex items-center justify-center shadow-[0_0_60px_rgba(2,199,195,0.55),0_0_120px_rgba(0,110,249,0.25)]">
              <span className="text-white text-2xl font-black tracking-tight select-none">QH</span>
            </div>
          </div>

          {/* "Query Health" wordmark subtitle */}
          <p className="text-white/50 text-sm italic mb-8 tracking-wide">Query Health</p>

          {/* Welcome heading */}
          <h1 className="text-[38px] font-bold text-white leading-tight mb-4">
            {t('welcome.headline')}
          </h1>

          {/* Body */}
          <p className="text-white/65 text-[16px] leading-relaxed mb-10">
            {t('welcome.body')}
          </p>

          {/* Continue button */}
          <button
            onClick={() => navigate('/verify')}
            className="w-full h-14 rounded-2xl font-bold text-base text-[#006EF9] bg-white shadow-[0_8px_40px_rgba(255,255,255,0.25)] hover:shadow-[0_8px_60px_rgba(255,255,255,0.4)] hover:scale-[1.02] active:scale-95 transition-all duration-200"
          >
            {t('common.continue')}
          </button>

          {/* Passive escape clause */}
          <p className="mt-6 text-[12px] text-white/35 leading-relaxed italic">
            {t('welcome.escapeClause')}
          </p>
        </div>
      </div>

      <Branding variant="inverse" className="relative z-10 px-6 pb-6" />
    </div>
  )
}
